import {
  propOr,
  pipe,
  split,
  map,
  compose,
  toUpper,
  head,
  join,
  last,
  anyPass,
  isEmpty,
  isNil,
  reject
} from 'ramda'

import {UserType} from 'types/User'

export const getNamespacedUserParam = (
  name: string,
  user: UserType,
  nameSpace: string,
  defaultValue?: any
): any => propOr(defaultValue, `${nameSpace}/${name}`, user)

/**
 * Returns string created by combining first uppercased letters of each word in passed string
 *
 * @param name - any string value
 *
 * @returns {string} result string
 */
export const getInitials = (name: string): string =>
  pipe<[string], string[], string[], string>(
    split(' '),
    map(compose<[string], string, string>(toUpper, head)),
    join('')
  )(name || '')

/**
 * Returns one or two initials of full user name
 *
 * @param string - full user name
 *
 * @returns {string} string consists of first letters of first and last words in name
 */
export const getFirstAndLastInitials = (name: string): string => {
  const initials = getInitials(name)

  if (initials.length > 1) {
    return `${head(initials)}${last(initials)}`
  }

  return head(initials)
}

/**
 * Returns only the domain of the email with `@` prefix.
 * If it is not a valid domain, return an empty string
 *
 * @param string - email
 *
 * @returns {string} email domain prefixed with `@`
 */
export const getEmailDomain = (email: string): string => {
  if (!email) return

  const splitedEmail = email.split('@')
  if (splitedEmail.length <= 1) return

  const domain = splitedEmail[splitedEmail.length - 1]

  return `@${domain}`
}

/**
 * Receives the user and returns the user initial
 * based on the name, first name, last name, email, or phone number,
 * in that order.
 *
 * @param UserType - user
 *
 * @returns {string} the user initials
 */
export const getUserName = (user: Partial<UserType>): string =>
  head(
    reject(anyPass([isNil, isEmpty]))([
      user?.name,
      user?.firstName,
      user?.lastName,
      user?.email
    ])
  ) || ''
